import { bool, func } from 'prop-types';
import React, { useEffect } from 'react';
import { Col, FormGroup, Input, Label } from 'reactstrap';

import ShareInput from '../../../generic/ShareInput';
import SingleSelect from '../../../generic/SingleSelect';
import { getTooltipLabelWithText } from '../../../generic/Tooltip';
import { productCodes } from '../../../products/constants';
import { opportunityShape } from '../../../prop-types';
import {
  businessModelValues,
  coInvestTypes,
  getBillingTypes,
  negotiatedByOptions,
  salesChannels,
} from '../../constants';
import HELP_TEXTS from '../../help-texts';

function OpportunityAdditionalDetails({
  model,
  newModel,
  readOnly,
  onChange,
  isSalesChannelSelfService,
  hasPcLeadRole,
  isSameSalesUnitAsOwner,
  embeddedInAgreement,
  isSalesforceOpp,
}) {
  const {
    billingType,
    coInvestType,
    negotiatedBy,
    salesChannel,
    comment,
    probability,
    selfServiceNCode,
    excludeFromInvoiceAutomation,
    hasCCPCampaignLevelSteering,
    hasCFCampaignLevelSteering,
    businessModel,
    associatedProduct,
  } = model;
  const inputProps = { onChange, readOnly };

  useEffect(() => {
    if (!onChange) return;

    const availableBillingTypes = getBillingTypes(salesChannel);
    const isCurrentBillingTypeAllowed = availableBillingTypes.some(
      type => type.value === billingType,
    );

    if (!isCurrentBillingTypeAllowed) {
      onChange({ target: { name: 'billingType', value: 'External' } });
    }
  }, [salesChannel, billingType, onChange]);

  const TooltipLabel = getTooltipLabelWithText(HELP_TEXTS);

  const isSSCB =
    isSalesChannelSelfService &&
    associatedProduct?.code === productCodes.M1_WEB_LABEL;

  const isWholesale = businessModel === businessModelValues.WHOLESALE;

  const billingTypeReadOnly =
    (hasPcLeadRole &&
      !isSameSalesUnitAsOwner &&
      isSalesChannelSelfService &&
      !isSSCB &&
      !isWholesale) ||
    !onChange;

  return (
    <>
      <div className="mb-5">
        <FormGroup row>
          <Label sm={4} for="op-coInvestType">
            Co-Invest Type
          </Label>
          <Col sm={8}>
            <SingleSelect
              options={coInvestTypes}
              id="op-coInvestType"
              name="coInvestType"
              value={coInvestType}
              {...inputProps}
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm={4} for="op-billingType" className="required">
            Billing Type
          </Label>
          <Col sm={8}>
            <SingleSelect
              options={getBillingTypes(salesChannel)}
              id="op-billingType"
              name="billingType"
              value={billingType}
              {...inputProps}
              readOnly={billingTypeReadOnly || isSalesforceOpp}
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm={4} for="op-salesChannel" className="required">
            Sales Channel
          </Label>
          <Col sm={8}>
            <SingleSelect
              options={salesChannels}
              id="op-salesChannel"
              name="salesChannel"
              value={salesChannel}
              isOptionDisabled={option => option.disabled}
              {...inputProps}
            />
          </Col>
        </FormGroup>
        {isSalesChannelSelfService && (
          <FormGroup row>
            <Label sm={4} for="op-selfServiceNCode" className="required">
              Self-Service Campaign Id
            </Label>
            <Col sm={8}>
              <Input
                id="op-selfServiceNCode"
                name="selfServiceNCode"
                value={selfServiceNCode}
                {...inputProps}
              />
            </Col>
          </FormGroup>
        )}
        {isSalesChannelSelfService && (
          <FormGroup row>
            <Label sm={4} for="op-associatedProduct" className="required">
              Associated Product
            </Label>
            <Col sm={8}>
              <Input
                id="op-associatedProduct"
                name="associatedProduct"
                value={
                  associatedProduct
                    ? `${associatedProduct.name} (${associatedProduct.code})`
                    : ''
                }
                {...inputProps}
                readOnly
              />
            </Col>
          </FormGroup>
        )}
        <FormGroup row>
          <Label sm={4} for="op-negotiatedBy" className="required">
            Negotiated By
          </Label>
          <Col sm={8}>
            <SingleSelect
              options={negotiatedByOptions}
              id="op-negotiatedBy"
              name="negotiatedBy"
              value={negotiatedBy}
              {...inputProps}
              readOnly={readOnly || isSalesforceOpp}
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <TooltipLabel
            helpKey="hasCFCampaignLevelSteering"
            text="CF Campaign Level Steering"
            colWidth={4}
            section="info"
            resource="op"
          />
          <Col sm={8}>
            <Input
              type="checkbox"
              id="op-hasCFCampaignLevelSteering"
              name="hasCFCampaignLevelSteering"
              checked={hasCFCampaignLevelSteering}
              className="form-control form-checkbox"
              {...inputProps}
              disabled={readOnly}
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <TooltipLabel
            helpKey="hasCCPCampaignLevelSteering"
            text="CCP Campaign Level Steering"
            colWidth={4}
            section="info"
            resource="op"
          />
          <Col sm={8}>
            <Input
              type="checkbox"
              id="op-hasCCPCampaignLevelSteering"
              name="hasCCPCampaignLevelSteering"
              checked={hasCCPCampaignLevelSteering}
              className="form-control form-checkbox"
              {...inputProps}
              disabled={readOnly}
            />
          </Col>
        </FormGroup>
        {!newModel && !embeddedInAgreement && (
          <FormGroup row>
            <Label sm={4} for="op-probability">
              Probability
            </Label>
            <Col sm={8}>
              <ShareInput
                className="text-nowrap"
                value={probability}
                id="op-probability"
                name="probability"
                readOnly
              />
            </Col>
          </FormGroup>
        )}
      </div>
      <div className="mb-5">
        <FormGroup row>
          <Label sm={4} for="op-comment">
            Comment
          </Label>
          <Col sm={8}>
            <Input
              type="textarea"
              id="op-comment"
              name="comment"
              value={comment}
              {...inputProps}
            />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm={4} check for="op-excludeFromInvoiceAutomation">
            Exclude from Invoice Automation
          </Label>
          <Col sm={8}>
            <Input
              type="checkbox"
              id="ac-excludeFromInvoiceAutomation"
              name="excludeFromInvoiceAutomation"
              checked={excludeFromInvoiceAutomation}
              className="form-control form-checkbox"
              readOnly
              disabled
            />
          </Col>
        </FormGroup>
      </div>
    </>
  );
}

OpportunityAdditionalDetails.propTypes = {
  model: opportunityShape.isRequired,
  readOnly: bool,
  onChange: func,
  newModel: bool,
  embeddedInAgreement: bool,
  isSalesChannelSelfService: bool,
  hasPcLeadRole: bool,
  isSameSalesUnitAsOwner: bool,
  isSalesforceOpp: bool,
};
OpportunityAdditionalDetails.defaultProps = {
  onChange: undefined,
  readOnly: false,
  newModel: false,
  embeddedInAgreement: false,
  isSalesChannelSelfService: false,
  hasPcLeadRole: false,
  isSameSalesUnitAsOwner: false,
  isSalesforceOpp: false,
};

export default OpportunityAdditionalDetails;
